import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Radar } from "../components/Radar";
import { brandQuery, useBrand } from "../brandContext";
import { useScanActivity } from "../scanContext";

type Stats = {
  total: number;
  high: number;
  medium: number;
  low: number;
  sources: number;
  new24?: number;
  bySource: { source: string; count: number }[];
  copies: { host: string; source: string; count: number; max_score: number }[];
  lastScanAt: string | null;
};

function Sparkline({ points }: { points: number[] }) {
  const w = 220;
  const h = 44;
  const max = Math.max(1, ...points);
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const d = points
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - 4 - (v / max) * (h - 10)).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Динамика упоминаний"
    >
      <path d={d} fill="none" stroke="var(--mint)" strokeWidth="2" />
      {points.map((v, i) => (
        <circle
          key={i}
          cx={(i * step).toFixed(1)}
          cy={(h - 4 - (v / max) * (h - 10)).toFixed(1)}
          r="2"
          fill="var(--mint)"
        />
      ))}
    </svg>
  );
}

export function OverviewPage() {
  const { activeId } = useBrand();
  const { scanning } = useScanActivity();
  const [stats, setStats] = useState<Stats | null>(null);
  const [brand, setBrand] = useState("—");
  const [needsHuman, setNeedsHuman] = useState(0);
  const [daily, setDaily] = useState<{ day: string; count: number }[]>([]);
  const wasScanning = useRef(false);

  const reloadStats = () => {
    void Promise.all([
      fetch(`/api/stats${brandQuery(activeId)}`).then((r) => r.json()),
      fetch(`/api/brand${brandQuery(activeId)}`).then((r) => r.json()),
      fetch(
        `/api/stats/daily${activeId ? `?brandId=${activeId}&` : "?"}days=14`,
      ).then((r) => r.json()),
      fetch("/api/plugins").then((r) => r.json()),
    ]).then(([s, b, d, p]) => {
      setStats(s);
      setBrand(b.brand || "—");
      setDaily(d.items || []);
      setNeedsHuman(
        (p.items || []).filter((x: { needsHuman: boolean }) => x.needsHuman).length,
      );
    });
  };

  useEffect(() => {
    reloadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (wasScanning.current && !scanning) reloadStats();
    wasScanning.current = scanning;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, activeId]);

  const spark = useMemo(() => {
    const days = 14;
    const byDay = new Map(daily.map((d) => [d.day, d.count]));
    const out: number[] = [];
    for (let i = days - 1; i >= 0; i -= 1) {
      const dt = new Date(Date.now() - i * 86400_000).toISOString().slice(0, 10);
      out.push(byDay.get(dt) || 0);
    }
    return out;
  }, [daily]);

  const s = stats;
  const attention: { label: string; to: string; danger?: boolean }[] = [];
  if (needsHuman > 0)
    attention.push({
      label: `Парсеры ждут человека: ${needsHuman}`,
      to: "/parsers",
      danger: true,
    });
  if ((s?.new24 ?? 0) > 0)
    attention.push({ label: `Новые упоминания за 24ч: ${s?.new24}`, to: "/mentions?new=1" });
  if ((s?.high ?? 0) > 0)
    attention.push({ label: `Копии high: ${s?.high}`, to: "/copies", danger: true });

  const topCopies = (s?.copies || []).filter((c) => c.host).slice(0, 5);

  return (
    <>
      <div className="grid-radar">
        <div>
          <h1 className="hero-title">ВКУРСЕ</h1>
          <p className="hero-slogan">Вы в курсе о вашем бренде в интернете</p>
          <div className="metrics">
            <Link to="/mentions" className="metric metric-link">
              <div className="n">{s?.total ?? "—"}</div>
              <div className="l">упоминаний</div>
            </Link>
            <Link to="/copies" className="metric metric-link">
              <div className="n">{s?.high ?? "—"}</div>
              <div className="l">копии (high)</div>
            </Link>
            <Link to="/parsers" className="metric metric-link">
              <div className="n">{s?.sources ?? "—"}</div>
              <div className="l">источников</div>
            </Link>
            <Link to="/mentions?new=1" className="metric metric-link metric-new">
              <div className="n">{s?.new24 ?? "—"}</div>
              <div className="l">новых за 24ч</div>
            </Link>
          </div>
          <div className="spark-wrap">
            <div className="spark-title">Упоминания по дням · 14д</div>
            <Sparkline points={spark} />
          </div>
          <p style={{ color: "var(--muted)", marginTop: 14, maxWidth: 420 }}>
            Проект: <strong style={{ color: "var(--text)" }}>{brand}</strong>.
            Живой сбор по ключам бренда → скоринг → UI. Демо-данных нет.
          </p>
        </div>
        <Radar sources={s?.bySource || []} total={s?.total} />
      </div>

      <div className="grid-2" style={{ marginTop: 18 }}>
        <div className="panel">
          <h3>Требует внимания</h3>
          {attention.length === 0 ? (
            <div className="empty">Всё чисто — новых копий и зависших парсеров нет.</div>
          ) : (
            attention.map((a) => (
              <Link
                key={a.label}
                to={a.to}
                className={`attention-row${a.danger ? " danger" : ""}`}
              >
                <span className="attention-dot" />
                {a.label}
                <span className="attention-arrow">→</span>
              </Link>
            ))
          )}
        </div>
        <div className="panel">
          <h3>Топ копий</h3>
          {topCopies.length === 0 ? (
            <div className="empty">Пока нет medium/high — после скана появятся.</div>
          ) : (
            topCopies.map((c) => (
              <Link
                key={`${c.host}-${c.source}`}
                to={`/mentions?host=${encodeURIComponent(c.host)}`}
                className="attention-row"
              >
                <span className="attention-dot" />
                {c.host}
                <span className="attention-meta">
                  {c.count} · {c.max_score}
                </span>
                <span className="attention-arrow">→</span>
              </Link>
            ))
          )}
        </div>
      </div>

      <div className="footer-bar">
        <div>Мониторинг · окно 30д</div>
        <div className="legend">
          <span>
            <i className="a" /> Активность
          </span>
          <span>
            <i className="w" /> Слабый сигнал
          </span>
          <span>
            <i className="d" /> Требует внимания
          </span>
        </div>
      </div>
    </>
  );
}
